import type { BoxProps } from '@chakra-ui/react';
import { Box, useToken } from '@chakra-ui/react';
import type React from 'react';
import { useColorMode } from '@/components/ui/color-mode-utils';

export interface FaceliftScrollContainerProps extends BoxProps {
  /**
   * Minimum width of the content inside the scroll container
   * @default '800px'
   */
  minWidth?: string;

  /**
   * Height of the scroll container
   * @default '100%'
   */
  height?: string;

  /**
   * Width of the scroll container
   * @default '100%'
   */
  width?: string;

  /**
   * Show horizontal scrollbar
   * @default true
   */
  showScrollbarX?: boolean;

  /**
   * Show vertical scrollbar
   * @default false
   */
  showScrollbarY?: boolean;

  /**
   * Content to be rendered inside the scroll container
   */
  children: React.ReactNode;
}

const FaceliftScrollContainer: React.FC<FaceliftScrollContainerProps> = ({
  children,
  minWidth = '800px',
  height = '100%',
  width = '100%',
  showScrollbarX = true,
  showScrollbarY = false,
  ...rest
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  // Get colors from Chakra theme
  const [gray300, gray600, gray800] = useToken('colors', [
    'gray.300',
    'gray.600',
    'gray.800',
  ]);

  const scrollbarThumb = isDark ? gray600 : gray300;
  const scrollbarThumbHover = isDark ? gray800 : gray600;

  return (
    <Box
      height={height}
      width={width}
      overflowX={showScrollbarX ? 'auto' : 'hidden'}
      overflowY={showScrollbarY ? 'auto' : 'hidden'}
      css={{
        // Hide scrollbar by default
        '&::-webkit-scrollbar': {
          height: '0px',
          width: '0px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'transparent',
        },
        // Show scrollbar on hover
        '&:hover::-webkit-scrollbar': {
          height: '8px',
          width: '8px',
        },
        '&:hover::-webkit-scrollbar-track': {
          background: 'transparent',
          borderRadius: '4px',
        },
        '&:hover::-webkit-scrollbar-thumb': {
          background: scrollbarThumb,
          borderRadius: '4px',
        },
        '&:hover::-webkit-scrollbar-thumb:hover': {
          background: scrollbarThumbHover,
        },
        '&::-webkit-scrollbar-thumb:active': {
          background: scrollbarThumbHover,
        },
        // Firefox
        scrollbarWidth: 'none',
        '&:hover': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${scrollbarThumb} transparent`,
        },
      }}
      {...rest}
    >
      <Box minWidth={minWidth} height="100%">
        {children}
      </Box>
    </Box>
  );
};

export default FaceliftScrollContainer;
